import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { getAllocationLog } from '../../../services/asset.service';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGrid, GridOverlay } from '@mui/x-data-grid';
import * as CryptoJS from "crypto-js";

/* ─── Shared Asset List CSS ─── */
import '../../ExcelUploads/AssetInfo/AssetInfo.css';
import '../../ExcelUploads/AssetInfo/AssetList.css';

function getFormatedDate(dateString) {
  if (dateString) {
    let dte = new Date(dateString);
    if (isNaN(dte.getTime())) return '';
    let year = dte.getFullYear();
    let month = dte.getMonth() + 1;
    let day = dte.getDate();
    if (month < 10) {
      month = '0' + month;
    }
    if (day < 10) {
      day = '0' + day;
    }
    return year + '-' + month + '-' + day;
  } else {
    return '';
  }
}

function getFormatedDateTime(dateString) {
  if (dateString) {
    let dte = new Date(dateString);
    if (isNaN(dte.getTime())) return '';
    let year = dte.getFullYear();
    let month = String(dte.getMonth() + 1).padStart(2, '0');
    let day = String(dte.getDate()).padStart(2, '0');
    let hours = String(dte.getHours()).padStart(2, '0');
    let minutes = String(dte.getMinutes()).padStart(2, '0');
    let seconds = String(dte.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } else {
    return '';
  }
}

const CustomLoader = () => (
  <GridOverlay>
    <CircularProgress color="primary" size={30} />
  </GridOverlay>
);

const columns = [
  { field: "serialNumber", hideable: true, hide: false, headerName: "S. No.", width: 90 },
  {
    field: "tag", hide: false, headerName: "TAG", width: 240,
    renderCell: (params) => (
      <span style={{ fontWeight: 600, color: '#1a3a5c' }}>{params.value}</span>
    )
  },
  { field: "ain_no", hide: false, headerName: "AIN", width: 180 },
  {
    field: "reason", hide: false, headerName: "Form Type", width: 140,
    renderCell: (params) => {
      const isAllocate = !params.row.reason || params.row.reason === '' || params.row.reason === "allocate" || params.row.reason === "Allocate from Web" || params.row.reason === "Allocate" || params.row.reason === "Re-allocate";
      return (
        <span className={`badge ${isAllocate ? 'bg-success' : 'bg-warning text-dark'}`} style={{ padding: '6px 12px', borderRadius: '12px', fontSize: '0.78rem' }}>
          {isAllocate ? 'Allocate' : 'Unallocate'}
        </span>
      );
    }
  },
  {
    field: "effective_date", hide: false, headerName: "Effective Date", width: 150,
    renderCell: (params) => (
      <span>{(params.row.effective_date != null) ? getFormatedDate(params.row.effective_date) : getFormatedDate(params.row.unallocate_date)}</span>
    )
  },
  {
    field: "created_at", hide: false, headerName: "Created Date", width: 180,
    renderCell: (params) => (
      <span>{getFormatedDateTime(params.row.created_at)}</span>
    )
  },
  {
    field: "Action", hide: false, headerName: "Action", width: 160,
    renderCell: (params) => {
      let EncryptTag = CryptoJS.AES.encrypt(JSON.stringify({
        'tag': params.row.tag,
      }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();
      let EncryptLogId = CryptoJS.AES.encrypt(JSON.stringify({
        'log_id': params.row.id,
      }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();
      return (
        <a className="al-btn al-btn-outline al-btn-sm" href={`/allocation-log-details?tag=${EncryptTag}&LogId=${EncryptLogId}`}>
          <i className="bi bi-eye-fill"></i> View Details
        </a>
      );
    }
  },
];

function AllocationLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const Tag = query.get('tag');

  useEffect(() => {
    if (Tag) {
      setLoading(true);
      try {
        const decryptedBytes = CryptoJS.AES.decrypt(Tag.replace(/ /g, '+'), process.env.REACT_APP_ENCRYPT_SECRET_KEY);
        const decrypt_data = JSON.parse(decryptedBytes.toString(CryptoJS.enc.Utf8));
        const decryptedTag = decrypt_data.tag;

        let EncryptData = CryptoJS.AES.encrypt(JSON.stringify({
          'param_data': { 'tag': decryptedTag },
          'authToken': sessionStorage.getItem('LoggedInToken'),
        }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();
        let oData = { 'token': EncryptData };
        getAllocationLog(oData).then(function (res) {
          if (res?.data?.data?.length) setItems(res.data.data);
          else setItems([]);
        }).finally(() => {
          setLoading(false);
        });
      } catch (err) {
        console.error('Error fetching allocation log:', err);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [Tag]);

  const dataWithSerialNumbers = items.map((item, index) => ({
    ...item,
    serialNumber: index + 1,
  }));

  return (
    <div className="al-page" id="allocation-log-page">
      {/* ══ PAGE HEADER ══ */}
      <div className="al-page-header" id="al-page-header">
        <div className="al-page-header-left">
          <div className="al-page-header-icon">
            <i className="bi bi-clock-history" />
          </div>
          <div className="al-page-header-text">
            <h5 className="al-page-title">Allocation Log</h5>
            <p className="al-page-subtitle">View historical allocation and unallocation audit logs for asset</p>
          </div>
        </div>
        <div className="al-record-badge">
          <a href={'/view-allocation'} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <i className="bi bi-arrow-left"></i> View Allocation List
          </a>
        </div>
      </div>

      {/* ══ DATA GRID CARD ══ */}
      <div className="al-grid-card" id="al-grid-card">
        <div className="al-grid-card-header">
          <span className="al-grid-card-title">
            <i className="bi bi-journal-text" /> Allocation Logs
          </span>
          {!loading && items.length > 0 && (
            <span className="al-showing-badge">
              Total Records: <strong>{items.length}</strong>
            </span>
          )}
        </div>

        <Box>
          <DataGrid
            rows={dataWithSerialNumbers}
            getRowId={(row) => row.id}
            columns={columns}
            pageSize={50}
            rowsPerPageOptions={[10, 25, 50, 100]}
            autoHeight
            loading={loading}
            components={{
              LoadingOverlay: CustomLoader,
            }}
            sx={{
              border: 'none',
              fontFamily: "var(--font-body)",
              '& .MuiDataGrid-columnHeaders': {
                color: '#fff', fontSize: '0.75rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': { color: '#000', fontSize: '0.88rem' },
              '& .MuiDataGrid-sortIcon': { color: 'rgb(46 45 45 / 85%)' },
              '& .MuiDataGrid-menuIconButton': { color: 'rgb(46 45 45 / 85%)' },
              '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': { color: 'rgba(255,255,255,0.85)' },
              '& .MuiDataGrid-row': { fontSize: '0.8rem' },
              '& .MuiDataGrid-row:hover': { backgroundColor: '#eef7fd' },
              '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#d8f0fa' },
              '& .MuiDataGrid-cell': { borderColor: '#f0f2f5' },
              '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #e2e6ef', background: '#f8f9fc' },
              '& .MuiDataGrid-virtualScroller': { backgroundColor: '#fff' },
            }}
          />
        </Box>
      </div>
    </div>
  );
}

export default AllocationLog;
